import React, { useState, useEffect, useRef } from 'react';
import styles from './Q_ScrollBar.module.css';
import cn from 'classnames';

interface Q_ScrollBarProps {
  containerRef: React.RefObject<HTMLElement>;
  className?: string;
}

export default function Q_ScrollBar({
  containerRef,
  className,
}: Q_ScrollBarProps) {
  const [scrollTop, setScrollTop] = useState(0);
  const [scrollHeight, setScrollHeight] = useState(0);
  const [clientHeight, setClientHeight] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragStartScrollTop, setDragStartScrollTop] = useState(0);
  const scrollbarRef = useRef<HTMLDivElement>(null);

  const updateScrollInfo = React.useCallback(() => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      setScrollTop(scrollTop);
      setScrollHeight(scrollHeight);
      setClientHeight(clientHeight);
    }
  }, [containerRef]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    updateScrollInfo();

    const handleScroll = () => updateScrollInfo();
    const handleResize = () => updateScrollInfo();

    container.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [containerRef, updateScrollInfo]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStartY(e.clientY);
    setDragStartScrollTop(scrollTop);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;

      const deltaY = e.clientY - dragStartY;
      const scrollbarHeight = scrollbarRef.current?.offsetHeight || 0;
      const trackHeight =
        scrollbarRef.current?.parentElement?.offsetHeight || 0;
      const scrollableHeight = trackHeight - scrollbarHeight;

      const scrollRatio = deltaY / scrollableHeight;
      const maxScrollTop = scrollHeight - clientHeight;
      const newScrollTop = Math.max(
        0,
        Math.min(maxScrollTop, dragStartScrollTop + scrollRatio * scrollHeight)
      );

      containerRef.current.scrollTop = newScrollTop;
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [
    isDragging,
    dragStartY,
    dragStartScrollTop,
    scrollHeight,
    clientHeight,
    containerRef,
  ]);

  const handleTrackClick = (e: React.MouseEvent) => {
    if (!containerRef.current || !scrollbarRef.current) return;

    const track = e.currentTarget as HTMLElement;
    const trackRect = track.getBoundingClientRect();
    const clickY = e.clientY - trackRect.top;
    const trackHeight = track.offsetHeight;
    const scrollbarHeight = scrollbarRef.current.offsetHeight;

    const clickRatio = clickY / (trackHeight - scrollbarHeight);
    const maxScrollTop = scrollHeight - clientHeight;
    const newScrollTop = clickRatio * maxScrollTop;

    containerRef.current.scrollTop = newScrollTop;
  };

  const scrollbarHeight = Math.max(10, (clientHeight / scrollHeight) * 100);
  const maxScrollTop = scrollHeight - clientHeight;
  const scrollbarTop =
    maxScrollTop > 0 ? (scrollTop / maxScrollTop) * (100 - scrollbarHeight) : 0;

  if (scrollHeight <= clientHeight) {
    return null;
  }

  return (
    <div className={cn(styles.wrapper, className)}>
      <div className={styles.track} onClick={handleTrackClick}>
        <div
          ref={scrollbarRef}
          className={cn(styles.scrollbar, isDragging && styles.dragging)}
          style={{
            height: `${scrollbarHeight}%`,
            top: `${scrollbarTop}%`,
          }}
          onMouseDown={handleMouseDown}
        />
      </div>
    </div>
  );
}
